import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Mail,
  MapPin,
  Phone,
  Send,
  Calendar,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { config } from "@/config";

const Contact: React.FC = () => {
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaValue) {
      toast({
        title: "Verification Required",
        description: "Please complete the reCAPTCHA verification.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);

    try {
      const formData = new FormData(e.target as HTMLFormElement);
      const data = {
        name: formData.get("name"),
        email: formData.get("email"),
        subject: formData.get("subject"),
        message: formData.get("message"),
        recaptchaToken: captchaValue,
      };

      const response = await fetch(`${config.backendUrl}/api/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send message");
      }

      setIsSuccess(true);
      toast({
        title: "Message sent!",
        description:
          "Thank you for your message. I'll get back to you as soon as possible.",
      });

      // Reset form and sending state
      (e.target as HTMLFormElement).reset();
      setCaptchaValue(null);
      recaptchaRef.current?.reset();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const handleSendAnother = () => {
    setIsSuccess(false);
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container-custom">
        <h2 className="section-title">Get In Touch</h2>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="bg-white shadow-lg rounded-lg p-8 border border-gray-100 transform transition-all duration-500 hover:shadow-xl relative">
            {isSuccess && (
              <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center p-8 z-10">
                <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                  Message Sent!
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Thank you for reaching out. I'll get back to you as soon as
                  possible.
                </p>
                <Button onClick={handleSendAnother} className="button-primary">
                  Send Another Message
                </Button>
              </div>
            )}
            <h3 className="text-2xl font-semibold mb-6 text-portfolio-blue">
              Send Me a Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Your name"
                  required
                  className="focus:ring-portfolio-blue focus:border-portfolio-blue"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your.email@example.com"
                  required
                  className="focus:ring-portfolio-blue focus:border-portfolio-blue"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="subject"
                  className="text-sm font-medium text-gray-700"
                >
                  Subject
                </label>
                <Input
                  id="subject"
                  name="subject"
                  placeholder="Subject of your message"
                  required
                  className="focus:ring-portfolio-blue focus:border-portfolio-blue"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="message"
                  className="text-sm font-medium text-gray-700"
                >
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Your message..."
                  rows={5}
                  required
                  className="focus:ring-portfolio-blue focus:border-portfolio-blue"
                />
              </div>

              <div className="flex justify-center mb-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LfJk0MrAAAAAGE4uckviAI8dfTALr_FvAf7CE0i"
                  onChange={handleCaptchaChange}
                  size="normal"
                  theme="light"
                />
              </div>

              <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
                <Button
                  type="submit"
                  className={`button-primary flex-1 flex items-center justify-center transition-all duration-300 ${
                    isSending ? "animate-pulse" : ""
                  }`}
                  disabled={isSending}
                >
                  <Send
                    size={18}
                    className={`mr-2 ${isSending ? "animate-bounce" : ""}`}
                  />
                  {isSending ? "Sending..." : "Send Message"}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 flex items-center justify-center"
                  asChild
                >
                  <Link to="/schedule">
                    <Calendar size={18} className="mr-2" />
                    Schedule a Call
                  </Link>
                </Button>
              </div>
            </form>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold mb-6 text-portfolio-blue">
                Contact Information
              </h3>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-portfolio-blue/10 p-3 rounded-full">
                    <Mail size={20} className="text-portfolio-blue" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Email</h4>
                    <a
                      href="mailto:anassech132@gmail.com"
                      className="text-gray-600 hover:text-portfolio-blue transition-colors"
                    >
                      anassech132@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-portfolio-blue/10 p-3 rounded-full">
                    <Phone size={20} className="text-portfolio-blue" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Phone</h4>
                    <a
                      href="tel:+212651108621"
                      className="text-gray-600 hover:text-portfolio-blue transition-colors"
                    >
                      +212 651108621
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-portfolio-blue/10 p-3 rounded-full">
                    <MapPin size={20} className="text-portfolio-blue" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Location</h4>
                    <p className="text-gray-600">Rabat, Morocco</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-6 text-portfolio-blue">
                Availability
              </h3>
              <p className="text-gray-600 mb-4">
                I'm currently{" "}
                <span className="text-green-600 font-medium">available</span>{" "}
                for freelance work and full-time opportunities.
              </p>
              <p className="text-gray-600 mb-4">
                My preferred working hours are 9 AM - 6 PM GMT+1, but I'm
                flexible for meetings across different time zones.
              </p>
              <p className="text-gray-600">
                Typical response time:{" "}
                <span className="font-medium">Within 24 hours</span>
              </p>

              <div className="mt-8">
                <Link
                  to="/schedule"
                  className="inline-flex items-center justify-center px-6 py-3 bg-portfolio-blue text-white rounded-lg shadow-md hover:bg-portfolio-blue/90 transition-colors"
                >
                  <Calendar size={18} className="mr-2" />
                  Book a Meeting
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
